import { useCallback, useState } from "react";
import type { Identifier } from "ra-core";
import { useDataProvider } from "ra-core";

import type { CrmDataProvider } from "../providers/types";
import type {
  AiCopilotRequest,
  AiCopilotResponse,
  AiDecision,
  AiErrorCode,
  DealAiSuggestion,
} from "../types";
import { AiCopilotError, aiErrorMessage, toAiErrorCode } from "./aiErrors";

/**
 * Almikia Epic EI (TASK-003): wraps the copilot data-provider calls with
 * loading/error state. Never throws into the calling screen: a failure
 * resolves to `null` and sets `error` (Spanish, user-facing) / `errorCode`.
 */
export const useAiCopilot = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<AiErrorCode | null>(null);

  const fail = useCallback((e: unknown) => {
    setErrorCode(e instanceof AiCopilotError ? e.code : toAiErrorCode(null));
    setError(aiErrorMessage(e));
  }, []);

  const request = useCallback(
    async (input: AiCopilotRequest): Promise<AiCopilotResponse | null> => {
      setIsLoading(true);
      setError(null);
      setErrorCode(null);
      try {
        return await dataProvider.requestAiSuggestions(input);
      } catch (e) {
        fail(e);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [dataProvider, fail],
  );

  const decide = useCallback(
    async (
      id: Identifier,
      decision: AiDecision,
    ): Promise<DealAiSuggestion | null> => {
      setError(null);
      setErrorCode(null);
      try {
        return await dataProvider.decideAiSuggestion(id, decision);
      } catch (e) {
        // Decision errors are already Spanish DB/provider messages, not AI codes.
        setError(e instanceof Error ? e.message : aiErrorMessage(e));
        return null;
      }
    },
    [dataProvider],
  );

  const reset = useCallback(() => {
    setError(null);
    setErrorCode(null);
  }, []);

  return { request, decide, reset, isLoading, error, errorCode };
};
